// src/pages/admin/ReviewsRatingsPage.jsx

import React, { useState, useEffect, useContext } from 'react';
import { Row, Col } from 'react-bootstrap';
import { FaStar, FaRegStar, FaStarHalfAlt, FaTractor, FaQuoteLeft } from 'react-icons/fa';
import { FiThumbsUp, FiMessageSquare, FiAward } from 'react-icons/fi';
import axios from 'axios';
import AppContext from '../../../context/AppContext.js';
import RentalAdminLayout from '../../../layout/equipmentrentaladmin/RentalAdminLayout.js';
import '../../../assets/css/equipmentrentaladmin/reviews/Reviews.css';

/* ── Render Stars ── */
const RenderStars = ({ rating, size = 16 }) => {
  const stars = [];
  const full  = Math.floor(rating);
  const half  = rating % 1 !== 0;
  for (let i = 0; i < full; i++)
    stars.push(<FaStar key={`f${i}`} style={{ color: '#FDB714', fontSize: size }} />);
  if (half)
    stars.push(<FaStarHalfAlt key="h" style={{ color: '#FDB714', fontSize: size }} />);
  for (let i = 0; i < 5 - Math.ceil(rating); i++)
    stars.push(<FaRegStar key={`e${i}`} style={{ color: '#FDB714', fontSize: size }} />);
  return <span className="rr-stars-row">{stars}</span>;
};

/* ── Rating Bar ── */
const RatingBar = ({ star, count, total }) => {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="rr-rating-bar-row">
      <span className="rr-bar-label">{star} ★</span>
      <div className="rr-bar-track">
        <div className="rr-bar-fill" style={{ width: `${pct}%` }} />
      </div>
      <span className="rr-bar-pct">{pct}%</span>
      <span className="rr-bar-count">({count})</span>
    </div>
  );
};

const ReviewsRatingsPage = () => {
  const { baseUrl, loggedInRental ,authToken} = useContext(AppContext);

  const [filterRating, setFilterRating] = useState('all');
  const [helpfulMap,   setHelpfulMap]   = useState({});
  const [reviewsData,  setReviewsData]  = useState([]);
  const [loading,      setLoading]      = useState(true);

  // ── FETCH REVIEWS ─────────────────────────────────────────────
  const fetchReviews = () => {
    if (!loggedInRental) return;
    setLoading(true);

    const formData = new FormData();
    formData.append('srrentaladmin_id', loggedInRental.id);

    axios.post(`${baseUrl}/rentaladmin/reviews/getReviewsByRentalAdmin`, formData, {
      headers: { 'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${authToken}`
       },
    }).then((res) => {
      if (res.data.success) setReviewsData(res.data.reviews || []);
      else setReviewsData([]);
      setLoading(false);
    }).catch(() => {
      setReviewsData([]);
      setLoading(false);
    });
  };

  useEffect(() => {
    if (loggedInRental) fetchReviews();
  }, [loggedInRental]);

  // ── COMPUTED STATS ────────────────────────────────────────────
  const totalReviews = reviewsData.length;
  const avgRating    = totalReviews > 0
    ? (reviewsData.reduce((a, r) => a + r.rating, 0) / totalReviews).toFixed(1)
    : '0.0';
  const fiveStars  = reviewsData.filter(r => r.rating === 5).length;
  const fourStars  = reviewsData.filter(r => r.rating === 4).length;
  const threeStars = reviewsData.filter(r => r.rating === 3).length;
  const twoStars   = reviewsData.filter(r => r.rating === 2).length;
  const oneStar    = reviewsData.filter(r => r.rating === 1).length;
  const totalHelpful = reviewsData.reduce((a, r) => a + (r.helpful || 0), 0);

  const statsData = [
    { icon: <FaStar />,          value: avgRating,    label: 'Average Rating', sub: 'Out of 5.0'  },
    { icon: <FiMessageSquare />, value: totalReviews, label: 'Total Reviews',  sub: 'All time'    },
    { icon: <FiThumbsUp />,      value: totalHelpful, label: 'Helpful Votes',  sub: 'Received'    },
    { icon: <FiAward />,         value: fiveStars,    label: '5-Star Reviews', sub: totalReviews > 0 ? `${Math.round((fiveStars / totalReviews) * 100)}% of total` : '0%' },
  ];

  // ── FILTER ────────────────────────────────────────────────────
  const filtered = filterRating === 'all'
    ? reviewsData
    : reviewsData.filter(r => r.rating === parseInt(filterRating));

  const handleHelpful = (id, current) => {
    setHelpfulMap(prev => ({ ...prev, [id]: prev[id] ? prev[id] : (current || 0) + 1 }));
  };

  // ================================================================
  // RETURN
  // ================================================================
  return (
    <RentalAdminLayout>
      <div className="rr-page">

        {/* PAGE HEADER */}
        <div className="rr-page-header">
          <h2 className="rr-page-title">Reviews & Ratings</h2>
          <p className="rr-page-sub">Manage customer feedback and ratings for your equipment</p>
        </div>

        {/* STAT CARDS */}
        <Row className="g-4 mb-4">
          {statsData.map((s, i) => (
            <Col key={i} xl={3} lg={3} md={6} sm={12}>
              <div className="rr-stat-card">
                <div className="rr-stat-icon">{s.icon}</div>
                <div className="rr-stat-content">
                  <h3>{s.value}</h3>
                  <p>{s.label}</p>
                  <span className="rr-stat-sub">{s.sub}</span>
                </div>
              </div>
            </Col>
          ))}
        </Row>

        {/* RATING BREAKDOWN */}
        <div className="rr-breakdown-card">
          <div className="rr-breakdown-top">
            <div className="rr-breakdown-left">
              <span className="rr-big-rating">{avgRating}</span>
              <RenderStars rating={parseFloat(avgRating)} size={24} />
              <p className="rr-big-sub">{totalReviews} reviews total</p>
            </div>
            <div className="rr-breakdown-bars">
              <RatingBar star={5} count={fiveStars}  total={totalReviews} />
              <RatingBar star={4} count={fourStars}  total={totalReviews} />
              <RatingBar star={3} count={threeStars} total={totalReviews} />
              <RatingBar star={2} count={twoStars}   total={totalReviews} />
              <RatingBar star={1} count={oneStar}    total={totalReviews} />
            </div>
          </div>

          <div className="rr-breakdown-divider" />

          <div className="rr-breakdown-filter">
            <span className="rr-filter-label">Filter by Rating:</span>
            <div className="rr-filter-chips">
              {['all', '5', '4', '3', '2', '1'].map(r => (
                <button
                  key={r}
                  className={`rr-filter-chip ${filterRating === r ? 'active' : ''}`}
                  onClick={() => setFilterRating(r)}
                >
                  {r === 'all' ? 'All Reviews' : `${r} ★`}
                </button>
              ))}
            </div>
            <span className="rr-showing-label">
              Showing <strong>{filtered.length}</strong> of {totalReviews}
            </span>
          </div>
        </div>

        {/* REVIEWS GRID */}
        {loading ? (
          <div className="rr-empty">
            <div className="rr-spinner"></div>
            <p style={{ marginTop: 12 }}>Loading reviews...</p>
          </div>
        ) : filtered.length > 0 ? (
          <div className="rr-reviews-grid">
            {filtered.map(review => (
              <div key={review.id} className="rr-review-card">

                {/* Card Header */}
                <div className="rr-card-header">
                  <div className="rr-customer-info">
                    <div className="rr-customer-avatar">{(review.customerName || 'C').charAt(0)}</div>
                    <div>
                      <div className="rr-customer-name">{review.customerName}</div>
                      <div className="rr-customer-city">{review.customerCity}</div>
                    </div>
                  </div>
                  <div className="rr-card-meta">
                    <RenderStars rating={review.rating} size={14} />
                    <span className="rr-card-date">{review.date}</span>
                  </div>
                </div>

                {/* Equipment Tag */}
                <div className="rr-equipment-tag">
                  <FaTractor style={{ color: '#FDB714', fontSize: 11 }} />
                  {review.equipmentName}
                </div>

                {/* Review Text */}
                <div className="rr-review-body">
                  <FaQuoteLeft className="rr-quote-icon" />
                  <p className="rr-review-text">{review.review}</p>
                </div>

                {/* Card Footer */}


              </div>
            ))}
          </div>
        ) : (
          <div className="rr-empty">
            <FaStar style={{ fontSize: 40, color: '#ddd' }} />
            <h4>No Reviews Found</h4>
            <p>{totalReviews === 0 ? 'No customers have left reviews yet.' : 'No reviews match the selected filter.'}</p>
          </div>
        )}

      </div>
    </RentalAdminLayout>
  );
};

export default ReviewsRatingsPage;